import { apiClient } from '@/services/api-client'
import { useAuthStore } from '@/store/auth-store'
import { isAnalyticsEnabled } from '@/features/analytics/services/analytics-config'
import {
  flush,
  flushOnUnload,
  getApiV1BaseUrl,
  setSessionId,
} from '@/features/analytics/services/analytics-tracker.service'

const SESSIONS_ENDPOINT = '/usage/sessions'
// sessionStorage (não localStorage) para cada aba ter seu próprio sessionId — evita condição de
// corrida ao encerrar sessão quando o usuário tem múltiplas abas abertas.
const SESSION_STORAGE_KEY = 'navega:analytics-session-id'

type SessionAction = 'start' | 'end'

interface ManageSessionRequestBody {
  sessionId: string
  action: SessionAction
  platform: 'web'
  appVersion: string
  occurredAt: string
}

function buildSessionBody(sessionId: string, action: SessionAction): ManageSessionRequestBody {
  return {
    sessionId,
    action,
    platform: 'web',
    appVersion: __APP_VERSION__,
    occurredAt: new Date().toISOString(),
  }
}

function getStoredSessionId(): string | null {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY)
  } catch (error) {
    console.warn('[analytics] falha ao ler sessionId de sessionStorage', error)
    return null
  }
}

function persistSessionId(sessionId: string): void {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId)
  } catch (error) {
    console.warn('[analytics] falha ao persistir sessionId em sessionStorage', error)
  }
}

function clearStoredSessionId(): void {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY)
  } catch (error) {
    console.warn('[analytics] falha ao limpar sessionId de sessionStorage', error)
  }
}

// Nunca lança: falha de rede/HTTP em /usage/sessions não pode quebrar login/logout.
async function sendSessionAction(sessionId: string, action: SessionAction): Promise<void> {
  const token = useAuthStore.getState().accessToken
  if (!token) {
    console.warn(
      `[analytics] sessão "${action}" descartada: nenhum token de autenticação disponível`,
    )
    return
  }

  try {
    await apiClient.post<unknown>(SESSIONS_ENDPOINT, buildSessionBody(sessionId, action), {
      token,
    })
  } catch (error) {
    console.warn(`[analytics] falha ao registrar sessão (action: "${action}")`, error)
  }
}

export async function startAnalyticsSession(): Promise<void> {
  if (!isAnalyticsEnabled()) return

  const sessionId = getStoredSessionId() ?? crypto.randomUUID()
  persistSessionId(sessionId)

  await sendSessionAction(sessionId, 'start')
  setSessionId(sessionId)
}

export async function endAnalyticsSession(): Promise<void> {
  if (!isAnalyticsEnabled()) return

  const sessionId = getStoredSessionId()

  // Descarrega a fila pendente antes de invalidar o sessionId corrente, para que os últimos
  // eventos ainda saiam atribuídos à sessão que está sendo encerrada.
  await flush()

  if (sessionId) {
    await sendSessionAction(sessionId, 'end')
    clearStoredSessionId()
  }

  setSessionId(null)
}

// Best-effort para beforeunload: descarrega eventos pendentes e encerra a sessão via fetch com
// keepalive, sem aguardar resposta e sem retry — não há tempo hábil antes do unload da aba.
export function endAnalyticsSessionOnUnload(): void {
  if (!isAnalyticsEnabled()) return

  const sessionId = getStoredSessionId()

  // Mesma ordem de endAnalyticsSession: descarrega a fila antes de invalidar o sessionId corrente.
  flushOnUnload()

  clearStoredSessionId()
  setSessionId(null)

  if (!sessionId) return

  const token = useAuthStore.getState().accessToken
  if (!token) return

  fetch(`${getApiV1BaseUrl()}${SESSIONS_ENDPOINT}`, {
    method: 'POST',
    keepalive: true,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(buildSessionBody(sessionId, 'end')),
  }).catch(() => {
    // Best-effort: a aba já pode estar fechando, não há como reagir a uma falha aqui.
  })
}
